import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Button } from 'react-aria-components';

import { WEB_CATALOG } from './catalog/generated-catalog';
import { AlphaExperience } from './features/alpha/AlphaExperience';
import { EditorSaveCoordinator } from './hooks/shift-draft-autosave';
import { WEB_NETWORK_GRAPH } from './network/generated-network-graph';
import { applyPendingAppUpdate, getAppPwaState, subscribeAppPwaState, type PwaState } from './pwa';
import { openAppStorage, type AppStorage } from './storage/app-storage';

type ThemePreference = 'dark' | 'light' | 'system';
type StorageStatus = 'error' | 'loading' | 'ready';

const THEME_STORAGE_KEY = 'dmf-theme-preference';
const DISPLAY_VERSION = '1.2.0';

function isThemePreference(value: string | null): value is ThemePreference {
  return value === 'dark' || value === 'light' || value === 'system';
}

function initialTheme(): ThemePreference {
  if (typeof window === 'undefined') {
    return 'system';
  }

  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return isThemePreference(stored) ? stored : 'system';
}

function pwaStatusLabel(state: PwaState): string {
  if (state.registration === 'error') {
    return 'Offline podpora se nepodařila spustit.';
  }
  if (state.update === 'applying') {
    return 'Aktivuji bezpečně připravenou aktualizaci aplikace.';
  }
  if (state.update === 'available') {
    return 'Je dostupná nová verze aplikace.';
  }
  if (state.update === 'failed') {
    return 'Aktualizaci se nepodařilo bezpečně aktivovat. Uložená data zůstala zachována.';
  }
  if (state.offlineReady) {
    return 'Aplikace je připravená pro příští spuštění bez sítě.';
  }
  switch (state.registration) {
    case 'registered':
    case 'registering':
      return 'Připravuji offline režim.';
    case 'idle':
      return 'Kontroluji offline režim.';
  }
}

function storageStatusLabel(status: StorageStatus): string {
  switch (status) {
    case 'ready':
      return 'Místní data jsou připravená a zůstávají v tomto zařízení.';
    case 'error':
      return 'Místní data se nepodařilo otevřít. Zápis je zablokovaný.';
    case 'loading':
      return 'Otevírám místní data.';
  }
}

export function App() {
  const [theme, setTheme] = useState<ThemePreference>(initialTheme);
  const pwaState = useSyncExternalStore(subscribeAppPwaState, getAppPwaState, getAppPwaState);
  const [storageStatus, setStorageStatus] = useState<StorageStatus>('loading');
  const [storage, setStorage] = useState<AppStorage | null>(null);
  const editorSaveCoordinator = useMemo(() => new EditorSaveCoordinator(), []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    let disposed = false;
    let activeStorage: AppStorage | null = null;

    void openAppStorage().then(
      (openedStorage) => {
        if (disposed) {
          openedStorage.close();
          return;
        }
        activeStorage = openedStorage;
        setStorage(openedStorage);
        setStorageStatus('ready');
      },
      () => {
        if (!disposed) {
          setStorageStatus('error');
        }
      },
    );

    return () => {
      disposed = true;
      activeStorage?.close();
    };
  }, []);

  return (
    <>
      <a className="skip-link" href="#main-content">
        Přeskočit na hlavní obsah
      </a>
      <div className="app-shell">
        <header className="app-header">
          <div className="brand-lockup">
            <span className="brand-mark" aria-hidden="true">
              <svg viewBox="0 0 48 48" focusable="false">
                <path d="M15 8h18M15 40h18M18 8l-3 32M30 8l3 32M13 16h22M12 24h24M13 32h22" />
              </svg>
            </span>
            <div>
              <p className="eyebrow">Pracovní deník jízd</p>
              <h1>Der Maschinfuehrer</h1>
            </div>
          </div>
          <span className="version-badge" aria-label={`Verze aplikace ${DISPLAY_VERSION}`}>
            {DISPLAY_VERSION}
          </span>
        </header>

        <main id="main-content" className="app-content" tabIndex={-1}>
          {storageStatus === 'ready' && storage !== null ? (
            <AlphaExperience storage={storage} saveCoordinator={editorSaveCoordinator} />
          ) : (
            <section className="system-card" aria-labelledby="storage-heading">
              <h2 id="storage-heading">Místní data</h2>
              <p role={storageStatus === 'error' ? 'alert' : 'status'}>
                {storageStatusLabel(storageStatus)}
              </p>
            </section>
          )}

          {pwaState.update === 'available' || pwaState.update === 'failed' ? (
            <section className="update-notice" aria-labelledby="update-heading">
              <div>
                <strong id="update-heading">Aktualizace aplikace</strong>
                <p aria-live="polite">{pwaStatusLabel(pwaState)}</p>
              </div>
              <Button
                className="primary-action touch-target"
                isDisabled={storageStatus !== 'ready'}
                onPress={() => {
                  void applyPendingAppUpdate(async () => {
                    if (storageStatus !== 'ready' || storage === null) {
                      throw new Error('Local writes are not ready for a safe application update.');
                    }
                    await editorSaveCoordinator.saveNow();
                    await storage.flushPendingWrites();
                  }).catch(() => undefined);
                }}
              >
                {pwaState.update === 'failed'
                  ? 'Zkusit aktualizaci znovu'
                  : 'Aktualizovat aplikaci'}
              </Button>
            </section>
          ) : null}

          <details className="app-about">
            <summary className="about-summary">
              <span>O aplikaci</span>
              <span>Verze {DISPLAY_VERSION}</span>
            </summary>
            <div className="about-content">
              <div className="about-intro">
                <p className="eyebrow">Der Maschinfuehrer</p>
                <h2>Soukromý offline deník jízd</h2>
                <p>
                  Verze {DISPLAY_VERSION} uchovává směny, úseky, trasy a zálohy přímo v tomto
                  zařízení. Nejde o železniční navigaci ani právně závaznou služební evidenci.
                </p>
              </div>

              <section className="about-section" aria-labelledby="appearance-heading">
                <h3 id="appearance-heading">Vzhled</h3>
                <label className="theme-control">
                  <span>Barevný režim</span>
                  <select
                    aria-label="Barevný vzhled"
                    className="touch-target"
                    value={theme}
                    onChange={(event) => setTheme(event.currentTarget.value as ThemePreference)}
                  >
                    <option value="system">Podle zařízení</option>
                    <option value="light">Světlý</option>
                    <option value="dark">Tmavý</option>
                  </select>
                </label>
              </section>

              <section className="about-section" aria-labelledby="offline-heading">
                <h3 id="offline-heading">Stav a bezpečí dat</h3>
                <p aria-live="polite">{pwaStatusLabel(pwaState)}</p>
                <p aria-live="polite">{storageStatusLabel(storageStatus)}</p>
                <ul className="limitations-list">
                  <li>
                    Bez cloudového účtu jsou data pouze v tomto zařízení. Pravidelně si stahuj
                    ověřenou JSON zálohu a uchovávej ji mimo prohlížeč.
                  </li>
                  <li>
                    Síťové návrhy používají plný přijatý routing core železniční sítě Česka a
                    Německa a pokrývají {WEB_NETWORK_GRAPH.coverageLabel}. Z 6 191 míst veřejného
                    katalogu je 6 125 napojeno. Návrh vznikne jen mezi místy, pro která graf najde
                    skutečnou souvislou cestu; jinak zůstávají funkční ruční, historické a šablonové
                    trasy. Pro propojenou dvojici se nabízí jedna nejkratší varianta podle délek
                    hran. Návrh není navigace ani potvrzení aktuální průjezdnosti.
                  </li>
                  <li>
                    Statistiky nejsou ve verzi {DISPLAY_VERSION} dostupné a aplikace je nenahrazuje
                    neověřeným odhadem.
                  </li>
                  <li>
                    Mobilní kompatibilita je automaticky ověřovaná v Chromium a WebKitu. WebKit je
                    emulace, nikoli důkaz testu na fyzickém iPhonu.
                  </li>
                </ul>
              </section>

              <section className="about-section" aria-labelledby="licence-heading">
                <h3 id="licence-heading">Licence a atribuce</h3>
                <p>{WEB_CATALOG.attribution}</p>
                <p>{WEB_NETWORK_GRAPH.attribution}</p>
                <p>
                  <a
                    className="touch-target"
                    href="https://www.openstreetmap.org/copyright"
                    target="_blank"
                    rel="noreferrer"
                  >
                    OpenStreetMap copyright a atribuce
                  </a>{' '}
                  ·{' '}
                  <a
                    className="touch-target"
                    href={WEB_NETWORK_GRAPH.licenseUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    ODbL 1.0
                  </a>{' '}
                  ·{' '}
                  <a
                    className="touch-target"
                    href={WEB_NETWORK_GRAPH.readmeUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    odpovídající databáze, původ a provedené úpravy grafu
                  </a>
                </p>
              </section>

              <details className="technical-details">
                <summary>Technické údaje</summary>
                <dl className="build-metadata">
                  {[
                    ['Verze aplikace', DISPLAY_VERSION],
                    ['Interní release', __BUILD_METADATA__.release_token],
                    ['Commit', __BUILD_METADATA__.commit_sha],
                    ['Build provenance', __BUILD_METADATA__.backup_provenance.build_id_sha256],
                    ['Katalog', __BUILD_METADATA__.catalog_version],
                    ['Graf', __BUILD_METADATA__.graph_version],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            </div>
          </details>
        </main>
      </div>
    </>
  );
}
